import {
  Box,
  Button,
  Input as ChakraInput,
  Select as ChakraSelect,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  SimpleGrid,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { format } from 'date-fns'
import React, { useContext, useEffect, useState } from 'react'
import { FieldError, SubmitHandler, useForm } from 'react-hook-form'
import { RiAddLine, RiDeleteBin6Line } from 'react-icons/ri'
import InputMask from 'react-input-mask'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import * as yup from 'yup'
import { Input } from '../../../../components/Form/Input'
import { Select } from '../../../../components/Form/Select'
import { useAuth } from '../../../../context/AuthContext'
import { ConfigurationContext } from '../../../../context/ConfigurationContext'
import { Product, ProductBurn } from '../../../../Interfaces/products'
import { RawMaterial } from '../../../../Interfaces/rawMaterial'
import { api } from '../../../../services/api'
import { accessOptions } from '../../../../utils/billingForms'
import { stepOptions } from '../../../../utils/serviceOrderSteps'

const createServiceOrderSchema = (canViewFinancialData: boolean) =>
  yup.object().shape({
    delivery_date: yup.string().required('Informe a data de entrega'),
    description: yup.string().required('Informe o nome do produto'),
    amount: yup.string().required('Informe a quantidade'),
    ink_color_id: yup.string().required('Informe a cor'),
    price: canViewFinancialData ? yup.string().required('Informe o valor') : yup.string(),
    billing_method: canViewFinancialData ? yup.string().required('Informe o método de cobrança') : yup.string(),
  })

interface Props {
  selectedProduct?: Product
  orderId: string
  onClose: () => void
}

export default function ProdcutsCreateOrUpdate({ selectedProduct, onClose, orderId }: Props) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { hasPermission } = useAuth()
  const { configuration } = useContext(ConfigurationContext)

  const canViewFinancialData = hasPermission('view_financial_data_on_demand')

  const id = selectedProduct?.id ?? ''

  const [burns, setBurns] = useState<ProductBurn[]>([])

  const { register, handleSubmit, formState, reset, getValues, setValue, watch } = useForm({
    resolver: yupResolver(createServiceOrderSchema(canViewFinancialData)),
  })

  const errors = formState.errors

  const { data: colors } = useQuery(
    'colors',
    async () => {
      const response = await api.get<RawMaterial[]>(`raw-materials`, {
        params: {
          category: 'TINTASCOLORIDAS',
        },
      })

      const select = response.data.map((x: RawMaterial) => ({
        label: `${x.name} - ${x.amount}KG`,
        value: x.id,
      }))

      return {
        colors: select,
      }
    },
    {
      retry: false,
    },
  )

  const isCreateMode = !id

  const addBurn = () => {
    const ovenBurnValue = Number(configuration?.oven_burn_value) || 0
    const newBurn: ProductBurn = {
      burn_value: ovenBurnValue / 3, // Valor padrão 1/3
      oven_space: 1, // 1 = 1/3, 2 = 1/2, 3 = 1/1
    }
    setBurns([...burns, newBurn])
  }

  const removeBurn = (index: number) => {
    setBurns(burns.filter((_, i) => i !== index))
  }

  const updateBurn = (index: number, field: 'burn_value' | 'oven_space', value: number) => {
    setBurns((prevBurns) => {
      const updatedBurns = [...prevBurns]
      updatedBurns[index] = { ...updatedBurns[index], [field]: value }
      return updatedBurns
    })
  }

  const updateBurnOvenSpace = (index: number, ovenSpace: number) => {
    setBurns((prevBurns) => {
      const updatedBurns = [...prevBurns]
      const calculatedValue = calculateBurnValue(ovenSpace)
      updatedBurns[index] = {
        ...updatedBurns[index],
        oven_space: ovenSpace,
        burn_value: calculatedValue,
      }
      return updatedBurns
    })
  }

  const calculateBurnValue = (ovenSpace: number): number => {
    const ovenBurnValue = Number(configuration?.oven_burn_value) || 0
    return (ovenSpace / 3) * ovenBurnValue
  }

  const createDemand = useMutation(
    async (product: Product) => {
      const date = product.delivery_date.split('/')

      const products = {
        ...product,
        delivery_date: format(new Date(`${date[2]}/${date[1]}/${date[0]}`), 'yyyy-MM-dd'),
        service_order_id: orderId,
        burns: burns, // Sempre envia o array, mesmo que vazio
      }

      const response = await api.post<Product>(`products`, {
        ...products,
      })
      if (response) {
        toast({ description: 'Cadastrado com sucesso.', status: 'success' })
        onClose()
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('serviceOrderProduct')
      },
      onError: (error: any) => {
        const errorList = error?.response?.data?.errors

        if (errorList) {
          const toastOptions = errorList.map((err) => ({ description: err.message, status: 'error' }))
          toastOptions.forEach((t) => {
            toast(t)
          })
        } else {
          toast({ description: 'Falha ao cadastrar', status: 'error' })
        }
      },
    },
  )

  useEffect(() => {
    if (id && selectedProduct) {
      reset({ ...selectedProduct, delivery_date: format(new Date(selectedProduct.delivery_date), 'dd/MM/yyyy') })
      // Carrega as queimadas existentes
      if (selectedProduct.burns && selectedProduct.burns.length > 0) {
        setBurns(selectedProduct.burns)
      } else {
        setBurns([])
      }
    } else {
      // Se for um novo produto, preenche com a data atual e primeira etapa
      reset({ 
        delivery_date: format(new Date(), 'dd/MM/yyyy'),
        step: stepOptions[0]?.value // Seleciona a primeira etapa
      })
      setBurns([])
    }
  }, [id, selectedProduct])

  useEffect(() => {}, [canViewFinancialData, setValue, getValues])

  useEffect(() => {
    if (!canViewFinancialData && !getValues('billing_method')) {
      setValue('billing_method', accessOptions[0].value)
    }
    if (!canViewFinancialData && !getValues('price')) {
      setValue('price', '0.00')
    }
  }, [setValue, getValues, canViewFinancialData])

  const updateDemands = useMutation(
    async (product: Product) => {
      const date = product.delivery_date.split('/')

      const products = {
        ...product,
        delivery_date: format(new Date(`${date[2]}/${date[1]}/${date[0]}`), 'yyyy-MM-dd'),
        service_order_id: orderId,
        burns: burns, // Sempre envia o array, mesmo que vazio
      }

      const response = await api.put<Product>(`products/${id}`, { ...products })

      if (response) {
        toast({ description: 'Editado com sucesso.' })
        onClose()
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('demands')
        queryClient.invalidateQueries('serviceOrderProduct')
      },
      onError: (error: any) => {
        const errorList = error?.response?.data?.errors

        if (errorList) {
          const toastOptions = errorList.map((err) => ({ description: err.message, status: 'error' }))
          toastOptions.forEach((t) => {
            toast(t)
          })
        } else {
          toast({ description: 'Falha ao cadastrar', status: 'error' })
        }
      },
    },
  )

  const handleCreateOrUpdate: SubmitHandler<Product> = async (values) => {
    isCreateMode ? await createDemand.mutateAsync(values) : await updateDemands.mutateAsync(values)
  }

  return (
    <Box flex="1" borderRadius="8" bg="gray.800" as="form" onSubmit={handleSubmit(handleCreateOrUpdate)}>
      <VStack spacing="8">
        <SimpleGrid minChildWidth="240px" spacing="8" width="100%">
          <Input
            label="Data"
            {...register('delivery_date')}
            error={errors.delivery_date as FieldError}
            as={InputMask}
            mask="99/99/9999"
          />
          <Input label="Nome do produto" {...register('description')} error={errors.description as FieldError} />
          <Input label="Quantidade" {...register('amount')} error={errors.amount as FieldError} />
          <Select label="Cores" {...register('ink_color_id')} options={colors?.colors} placeholder="Selecione um cor" />
        </SimpleGrid>
        <SimpleGrid minChildWidth="240px" spacing="8" width="100%">
          {canViewFinancialData && (
            <>
              <Input label="Valor (Unidade)" {...register('price')} error={errors.price as FieldError} />
              <Select
                label="Forma de cobrança"
                {...register('billing_method')}
                options={accessOptions}
                placeholder="Selecione"
              />
            </>
          )}

          <Select label="Etapa" {...register('step')} options={stepOptions} placeholder="Selecione" />
        </SimpleGrid>

        {/* Seção de Queimadas */}
        <Box width="100%" mt="4">
          <Flex justify="space-between" align="center" mb="4">
            <Box fontWeight="bold" fontSize="md">
              Queimadas
            </Box>
            <Button
              leftIcon={<Icon as={RiAddLine} />}
              size="sm"
              colorScheme="green"
              onClick={addBurn}>
              Adicionar Queimada
            </Button>
          </Flex>

          {burns.length > 0 && (
            <VStack spacing="4" align="stretch">
              {burns.map((burn, index) => (
                <Box
                  key={index}
                  p="4"
                  border="1px"
                  borderColor="gray.600"
                  borderRadius="md"
                  bg="gray.700">
                  <SimpleGrid minChildWidth="200px" spacing="4" width="100%">
                    <FormControl>
                      <FormLabel>Espaço no Forno</FormLabel>
                      <ChakraSelect
                        value={String(burn.oven_space || 1)}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          const ovenSpace = Number(e.target.value)
                          updateBurnOvenSpace(index, ovenSpace)
                        }}
                        bg="white"
                        color="blackAlpha.900">
                        <option value={1}>1/3</option>
                        <option value={2}>1/2</option>
                        <option value={3}>1/1</option>
                      </ChakraSelect>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Valor da Queimada</FormLabel>
                      <ChakraInput
                        type="number"
                        value={burn.burn_value || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateBurn(index, 'burn_value', Number(e.target.value) || 0)}
                        bg="white"
                        color="blackAlpha.900"
                      />
                    </FormControl>
                    <Flex align="end" pb="2">
                      <IconButton
                        aria-label="Remover queimada"
                        icon={<Icon as={RiDeleteBin6Line} />}
                        colorScheme="red"
                        size="md"
                        onClick={() => removeBurn(index)}
                      />
                    </Flex>
                  </SimpleGrid>
                </Box>
              ))}
            </VStack>
          )}

          {burns.length === 0 && (
            <Box p="4" textAlign="center" color="gray.400" fontSize="sm">
              Nenhuma queimada adicionada. Clique em &quot;Adicionar Queimada&quot; para começar.
            </Box>
          )}
        </Box>
      </VStack>

      <Flex mt="8" justify="flex-end">
        <HStack spacing="4">
          <Button type="submit" colorScheme="pink" isLoading={formState.isSubmitting}>
            Salvar
          </Button>
        </HStack>
      </Flex>
    </Box>
  )
}
